/* eslint-disable no-empty-function */
import {action, makeObservable, observable, reaction} from 'mobx';
import uid from '@mvps-genesis/uid';

export class Message {
	_parentStore;

	constructor ({duration = 8000, text, type = 'info', parentStore}) {
		this.id = uid();
		this._parentStore = parentStore;
		this.beforeRemoveHandler = null;
		this.processed = false;
		this.removing = false;
		this.timeoutId = null;
		this.duration = duration;
		this.text = text;
		this.type = type;

		makeObservable(this, {
			removing: observable
		});
	}

	stopAutoRemove () {
		this.processed = true;
		clearTimeout(this.timeoutId);
	}

	triggerAutoRemove () {
		if (this.timeoutId === null) {
			setTimeout(this.remove.bind(this), this.duration);
		}
	}

	beforeRemove () {
		return typeof this.beforeRemoveHandler === 'function'
			? this.beforeRemoveHandler()
			: () => {};
	}

	async remove () {
		this.removing = true;

		// Await any promise or animation
		await this.beforeRemove();

		this.processed = true;
		this.stopAutoRemove();
		this._parentStore.remove(this.id);
	}
};

export class MessagesStore {

	/** @type {Message[]} */
	messages = [];

	constructor () {
		makeObservable(this, {
			add: action,
			messages: observable.deep,
			remove: action
		});

		/** @type {import('mobx').IReactionDisposer} */
		this.disposer = reaction(
			() => this.messages.length,
			() => {
				const nextMessage = this.nextMessage();

				// Set to processed and create a timeout to auto-remove the message
				if (nextMessage) {
					nextMessage.triggerAutoRemove();
				}
			}
		);
	}

	/**
	 * Get next message in the queue
	 * @recursive
	 * @param {number} [start = 0] The starting index to check for an unprocessed message
	 * @returns {Message}
	 */
	nextMessage (start = 0) {
		const nextMessage = this.messages.slice(start, start + 1)[0];

		if (nextMessage && nextMessage.processed === true) {
			return this.nextMessage(start + 1);
		}

		return nextMessage;
	}

	add (options) {
		// Create new instance
		const message = new Message({
			...options,
			parentStore: this
		});

		// Push into array
		this.messages.push(message);
	}

	remove (id) {
		this.messages = this.messages.filter(message => message.id !== id);
	}
};
